import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import AuthLayout from './AuthLayout';
import AuthInput from './AuthInput';

interface SignInProps {
  isModal?: boolean;
  onClose?: () => void;
}

const SignIn: React.FC<SignInProps> = ({ isModal, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('signup_confirmed') === 'true') {
      setShowConfirmationPopup(true);
      setTimeout(() => {
        setShowConfirmationPopup(false);
      }, 5000); // Show for 5 seconds
      // Clean up the URL
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    console.log('Starting authentication attempt...');
    
    try {
      console.log('Calling supabase.auth.signInWithPassword with email:', email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log('Authentication response received:', error ? 'Error occurred' : 'Success');
      
      if (error) {
        console.error('Supabase auth error:', error);
        throw error;
      }

      console.log('Authentication successful, session:', data.session ? 'Valid' : 'Invalid');

      // Log DPA acceptance server-side after successful sign-in
      try {
        const { data: dpaData, error: dpaError } = await supabase.functions.invoke(
          'log-dpa-acceptance',
          {
            body: { version: '29.04.2025' },
          }
        );
        if (dpaError) {
          console.error('Edge Function error:', dpaError);
        } else {
          console.log('Function result:', dpaData);
        }
      } catch (err) {
        console.error('Edge Function network error:', err);
      }

      if (isModal && onClose) {
        onClose();
      } else {
        const returnPath = sessionStorage.getItem('returnPath');
        console.log('Return path from session:', returnPath);
        
        if (returnPath) {
          sessionStorage.removeItem('returnPath');
          const targetPath = returnPath.startsWith('/') ? `/${language}${returnPath}` : returnPath;
          console.log('Navigating to return path:', targetPath);
          navigate(targetPath);
        } else {
          console.log('Navigating to profile page');
          navigate(`/${language}/profile`);
        }
      }
    } catch (error: any) {
      console.error('Login error details:', error);
      // Display specific error message if available
      if (error.message) {
        setError(`${t('auth.errors.generic')}: ${error.message}`);
      } else {
        setError(t('auth.errors.generic'));
      }
    } finally {
      console.log('Setting isLoading to false');
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout titleKey="nav.signin" isModal={isModal} onClose={onClose}>
      {showConfirmationPopup && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-[var(--success)]/10 border border-[var(--success)]/20 rounded-lg p-4 text-center shadow-lg">
            <p className="text-[var(--success)]">{t('auth.emailConfirmedPopup')}</p>
          </div>
        </div>
      )}
      <h2 className="text-center text-3xl font-bold text-[var(--text-primary)]">
        {t('auth.signin')}
      </h2>
      
      <form onSubmit={handleSubmit} className="mt-8 space-y-6">
        <AuthInput
          id="email"
          type="email"
          label={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <AuthInput
          id="password"
          type="password"
          label={t('auth.passwordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />

        {error && (
          <p className="text-sm text-[var(--error)] text-center">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-[var(--brand-primary)] text-[var(--text-button)] !important rounded-md hover:bg-[var(--brand-primary)]/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? t('auth.signingIn') : t('auth.signin')}
        </button>

        <div className="text-center space-y-4">
          <p className="text-[var(--text-tertiary)]">
            {t('auth.noAccount')}{' '}
            <Link
              to={`/${language}/sign-up`}
              className="text-[var(--brand-primary)] hover:text-[var(--brand-primary)]/80"
            >
              {t('auth.signup')}
            </Link>
          </p>

          <Link
            to={`/${language}/auth/reset-password`}
            className="block text-[var(--brand-primary)] hover:text-[var(--brand-primary)]/80"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default SignIn;